import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cookie, Settings2, X } from "lucide-react";
import { logCookieConsent } from "@/lib/api";

const STORAGE_KEY = "biab_cookie_consent_v1";

const defaultPrefs = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
    else {
      try {
        setPrefs({ ...defaultPrefs, ...JSON.parse(saved) });
      } catch {
        setVisible(true);
      }
    }
    window.addEventListener("biab:open-cookie-preferences", () => {
      setShowManager(true);
      setVisible(true);
    });
  }, []);

  const persist = async (next) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setPrefs(next);
    setVisible(false);
    setShowManager(false);
    try {
      await logCookieConsent(next);
    } catch (e) {
      // non-blocking
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-[420px] glass-strong text-slate-100 p-6 rounded-2xl z-[100] flex flex-col gap-4 glow-cyan-soft"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-cyan-400/10 text-cyan-300 inline-flex items-center justify-center shrink-0 border border-cyan-400/30">
          <Cookie className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-base text-white">We value your privacy</h4>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed">
            We use cookies to enhance browsing experience and analyse traffic. Read our{" "}
            <a href="/cookie-policy" className="text-cyan-300 font-medium hover:underline">
              Cookie Policy
            </a>
            .
          </p>
        </div>
        <button
          data-testid="cookie-close-button"
          onClick={() => persist({ ...defaultPrefs })}
          className="text-slate-500 hover:text-white"
          aria-label="Reject non-essential and close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {showManager && (
        <div className="border-t border-white/[0.06] pt-4 space-y-3">
          {[
            { key: "essential", label: "Essential", desc: "Required for site to function.", disabled: true, alwaysOn: true },
            { key: "analytics", label: "Analytics", desc: "Anonymous usage statistics." },
            { key: "marketing", label: "Marketing", desc: "Personalised ads & campaigns." },
            { key: "preferences", label: "Preferences", desc: "Remember your settings." },
          ].map((opt) => (
            <div key={opt.key} className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold text-white">{opt.label}</Label>
                <p className="text-xs text-slate-500">{opt.desc}</p>
              </div>
              <Switch
                checked={opt.alwaysOn ? true : prefs[opt.key]}
                disabled={opt.disabled}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, [opt.key]: v }))}
                data-testid={`cookie-switch-${opt.key}`}
                className="data-[state=checked]:bg-cyan-400"
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Button
          data-testid="cookie-accept-all-button"
          onClick={() =>
            persist({ essential: true, analytics: true, marketing: true, preferences: true })
          }
          className="rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold w-full"
        >
          Accept All
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="cookie-reject-button"
            variant="outline"
            onClick={() => persist({ ...defaultPrefs })}
            className="rounded-full border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white font-semibold"
          >
            Reject Non-Essential
          </Button>
          {showManager ? (
            <Button
              data-testid="cookie-save-preferences-button"
              variant="outline"
              onClick={() => persist(prefs)}
              className="rounded-full border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white font-semibold"
            >
              Save Preferences
            </Button>
          ) : (
            <Button
              data-testid="cookie-manage-button"
              variant="outline"
              onClick={() => setShowManager(true)}
              className="rounded-full border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white font-semibold inline-flex items-center gap-2"
            >
              <Settings2 className="h-4 w-4" /> Manage
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
